import "../variables.module.css";

import { OmniboxResultRowAdapter } from "@mirage/mosaics/Omnibox/components/ResultRow/adapter/OmniboxResultRowAdapter";
import i18n from "@mirage/translations";
import classnames from "classnames";
import React, { CSSProperties, useMemo } from "react";

import type { TypeaheadResult } from "@mirage/mosaics/SearchBarWithTypeahead/useConvertToTypeaheadResults";
import {
  DROPDOWN_BANNER_HEIGHT_SELECTOR,
  DROPDOWN_LAUNCHER_MAX_HEIGHT_SELECTOR,
  DROPDOWN_MAX_HEIGHT_SELECTOR,
} from "../../constants";
import { useObserveVisibleResults } from "../../hooks/useObserveVisibleResults";
import { useResultsDynamicMaxHeight } from "../../hooks/useResultsDynamicMaxHeight";
import style from "./ResultList.module.css";

type Props = {
  isTypeaheadOpen: boolean;
  isDualModeLauncher: boolean;
  expandMotionVersion?: string;
  listContainerRef: React.RefObject<HTMLUListElement>;
  results: TypeaheadResult[];
  selectedIdx: number;
  setHoveredIdx: (i: number | null) => void;
  onResultClicked: (item: TypeaheadResult, index: number) => void;
  typeaheadQuery: string;
  isMouseActive: boolean;
  fullWidth: boolean;
  resultItemRefs: React.RefObject<HTMLLIElement>[];
  onListItemView: (item: TypeaheadResult, index: number) => void;
};

export function ResultList({
  isTypeaheadOpen,
  isDualModeLauncher,
  expandMotionVersion,
  listContainerRef,
  results,
  selectedIdx,
  setHoveredIdx,
  onResultClicked,
  typeaheadQuery,
  isMouseActive,
  fullWidth,
  resultItemRefs,
  onListItemView,
}: Props) {
  const { dynamicMaxHeight } = useResultsDynamicMaxHeight(
    isTypeaheadOpen && expandMotionVersion !== "V1",
    isDualModeLauncher,
  );

  const { intersectionObserver } = useObserveVisibleResults(
    results,
    isTypeaheadOpen,
    onListItemView,
  );

  const dynamicMaxHeightStyle = useMemo(() => {
    if (!dynamicMaxHeight) return {};

    return {
      [DROPDOWN_MAX_HEIGHT_SELECTOR]: `calc(${dynamicMaxHeight}px - var(${DROPDOWN_BANNER_HEIGHT_SELECTOR}))`,
      [DROPDOWN_LAUNCHER_MAX_HEIGHT_SELECTOR]: `calc(${dynamicMaxHeight}px - var(${DROPDOWN_BANNER_HEIGHT_SELECTOR}))`,
    } as CSSProperties;
  }, [dynamicMaxHeight]);

  return (
    <ul
      className={classnames(style.resultListContainer, {
        [style.isDualModeLauncher]: isDualModeLauncher,
      })}
      style={dynamicMaxHeightStyle}
      ref={listContainerRef}
      role="listbox"
      aria-label={i18n.t("aria_search_results_list")}
    >
      {results.map((result, i) => {
        const isFirstInSection =
          i === 0 ||
          results[i - 1].scoredResult.sectionId !==
            result.scoredResult.sectionId;

        return (
          <OmniboxResultRowAdapter
            result={result}
            key={`${result.type}:${result.uuid}`}
            isFirstInSection={isFirstInSection}
            resultRowIndex={i}
            isSelected={i === selectedIdx}
            setIsHovered={() => {
              setHoveredIdx(i);
            }}
            setIsUnhovered={() => setHoveredIdx(null)}
            onClick={() => {
              onResultClicked(result, i);
            }}
            // NOTE (AYC): move this out of a prop (avoid prop drilling) and use atom
            typeaheadQuery={typeaheadQuery}
            ref={resultItemRefs[i]}
            observer={intersectionObserver.current}
            isMouseActive={isMouseActive}
            fullWidth={fullWidth}
          />
        );
      })}
    </ul>
  );
}
